/**
 * OSS 分片上传公共方法
 * @param {File} file - 要上传的文件对象
 * @param {Object} options - 配置项
 * @param {Function} options.getInitApi - (file) => Promise<{ uploadId, objectName }> 获取初始化信息
 * @param {Function} options.getPartUrlApi - (objectName, uploadId, partNumber) => Promise<string> 获取分片签名URL
 * @param {Function} options.completeApi - (objectName, uploadId, parts) => Promise<{ url }> 完成上传
 * @param {Function} options.onProgress - (progress, uploadedCount, totalChunks) => void 进度回调
 * @param {number} [options.chunkSize=5*1024*1024] - 分片大小 (默认 4MB)
 * @param {number} [options.concurrent=3] - 最大并发数 (默认 3)
 * @param {number} [options.maxRetry=3] - 单个分片最大重试次数 (默认 3)
 * @returns {Promise<{ url: string, name: string }>} 上传结果
 */
/**
 * OSS 分片上传公共方法
 */
import { getInitApi, getPartUrlApi, completeApi } from "@/api";

async function uploadFileInChunks(file, options) {
  // ✅ 1. 记录开始时间 (高精度时间戳，单位：毫秒)
  const startTime = performance.now();

  console.log("=== 开始上传流程 ===");
  const {
    onProgress,
    chunkSize = 10 * 1024 * 1024, // 10MB
    concurrent = 3,
    maxRetry = 3,
  } = options;

  if (!file) throw new Error("文件不能为空");
  const fileSize = file.size;
  const totalChunks = Math.ceil(fileSize / chunkSize);
  let uploadedCount = 0;
  const parts = new Array(totalChunks).fill(null);

  let params = {
    fileName: file.name,
    voType: options.voType,
    voFileType: file.type,
    spot: options.spot,
    voTime: options.spTime,
  };

  try {
    // 1. 初始化上传
    console.log("正在初始化上传...");
    const initRes = await getInitApi(params);
    const { uploadId, objectName, fileId } = initRes;

    if (!uploadId || !objectName) {
      throw new Error("初始化失败：未获取到 uploadId 或 objectName");
    }

    // 2. 定义单个分片上传逻辑 (含重试)
    const uploadPart = async (partNumber, start, end) => {
      const chunk = file.slice(start, end);
      let retryCount = 0;

      while (retryCount < maxRetry) {
        try {
          const uploadUrl = await getPartUrlApi({
            objectName,
            uploadId,
            partNumber,
            fileId,
          });

          const response = await fetch(uploadUrl, {
            method: "PUT",
            body: chunk,
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const eTag = response.headers.get("etag")?.replace(/"/g, "");
          if (!eTag) throw new Error("未获取到 ETag");

          return { partNumber, eTag };
        } catch (error) {
          retryCount++;
          console.warn(
            `分片 ${partNumber} 上传失败，第 ${retryCount} 次重试...`,
            error,
          );
          if (retryCount >= maxRetry) {
            throw new Error(
              `分片 ${partNumber} 上传彻底失败: ${error.message}`,
            );
          }
          await new Promise((r) =>
            setTimeout(r, 1000 * Math.pow(2, retryCount)),
          );
        }
      }
    };

    // 3. 并发控制队列
    const queue = [];
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, fileSize);
      const partNumber = i + 1;
      queue.push(() => uploadPart(partNumber, start, end));
    }

    const runWorker = async () => {
      while (queue.length > 0) {
        const task = queue.shift();
        const result = await task();

        parts[result.partNumber - 1] = {
          partNumber: result.partNumber,
          eTag: result.eTag,
          partSize: "",
          partCRC: "",
        };

        uploadedCount++;
        if (onProgress) {
          const percent = Math.floor((uploadedCount / totalChunks) * 100);
          onProgress(percent, uploadedCount, totalChunks);
        }
      }
    };

    const workerCount = Math.min(concurrent, totalChunks);
    const workers = Array.from({ length: workerCount }, () => runWorker());

    await Promise.all(workers);

    // 4. 完成上传
    console.log("所有分片上传完成，正在合并...");
    const validParts = parts.filter((p) => p !== null);

    const completeRes = await completeApi({
      objectName,
      uploadId,
      partETags: validParts,
      fileId,
    });
    // ✅ 2. 计算总耗时
    const endTime = performance.now();
    const totalDuration = (endTime - startTime).toFixed(2); // 单位：毫秒

    // ✅ 3. 输出详细统计
    console.log("=== 上传流程结束 ===");
    console.log(`上传成功!`);
    console.log(
      `总耗时: ${totalDuration} ms (${(totalDuration / 1000).toFixed(2)} s)`,
    );
    console.log(`文件大小: ${(fileSize / 1024 / 1024).toFixed(2)} MB`);
    console.log(`分片数量: ${totalChunks}`);
    console.log(
      `平均速度: ${(fileSize / 1024 / 1024 / (totalDuration / 1000)).toFixed(
        2,
      )} MB/s`,
    );

    // 可选：将耗时信息也返回给调用者
    return {
      fileId,
      completeRes,
      _meta: {
        duration: parseFloat(totalDuration),
        fileSize: fileSize,
        totalChunks: totalChunks,
      },
    };
  } catch (error) {
    // 即使报错，也可以计算耗时
    const endTime = performance.now();
    const totalDuration = (endTime - startTime).toFixed(2);

    console.error("=== 上传流程失败 ===");
    console.error(`错误信息: ${error.message}`);
    console.log(`失败前耗时: ${totalDuration} ms`);

    throw error;
  }
}

export default uploadFileInChunks;
